<template>
  <content-dialog
    :visible.sync="modalVisible"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    :small-title="$vuetify.breakpoint.xsOnly"
    max-width="400px"
  >
    <!--<editor-fold desc="Dialog Title">-->
    <template slot="dialogTitle">
      Create a new cost report
    </template>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Content">-->
    <v-card-text :class="{'pa-4': $vuetify.breakpoint.smAndUp }">
      <yacht-modal-select
        v-model="yacht"
        :dialog-visible.sync="yachtModalSelectVisible"
        data-vv-as="Yacht"
        name="yacht"
        class="mb-2"
        outlined
        clearable
        hide-details
        :force-dialog="$vuetify.breakpoint.xsOnly"
        label="Select yacht"
        @submit="yachtModalSelectVisible = false"
      />

      <v-card
        v-if="yacht"
        class="radius-5 grey--text text--darken-3 pa-4 mb-2"
        color="grey lighten-4"
      >
        <div v-if="lastReport">
          Last report was created on {{ lastReport.created_at | moment('D MMM YYYY') }} by {{ lastReport.staff.name }}.
          It includes service from <strong>{{ lastReport.date_from | moment('D MMM YYYY') }}</strong> to
          <strong>{{ lastReport.date_to | moment('D MMM YYYY') }}</strong>.
        </div>
        <div v-else>
          No reports were previously created for this yacht.
        </div>
      </v-card>

      <div v-if="yacht && !loadingLastReport">
        <v-subheader>
          Create report
        </v-subheader>

        <v-menu
          v-model="dateFrom.picker"
          :close-on-content-click="false"
          transition="fade-transition"
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-model="dateFromValue"
              v-validate="'required'"
              data-vv-as="from"
              data-vv-name="date_from"
              :error-messages="veeErrors.collect('date_from')"
              label="From"
              outlined
              clearable
              append-icon="event"
              readonly
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="dateFrom.value"
            no-title
            :min="dateFrom.minDate"
            @change="dateFrom.picker = false"
          />
        </v-menu>

        <v-menu
          v-model="dateTo.picker"
          :close-on-content-click="false"
          transition="fade-transition"
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-model="dateToValue"
              v-validate="'required'"
              data-vv-as="to"
              data-vv-name="date_to"
              :error-messages="veeErrors.collect('date_to')"
              label="To"
              clearable
              outlined
              append-icon="event"
              readonly
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="dateTo.value"
            no-title
            :min="dateTo.minDate"
            @change="dateTo.picker = false"
          />
        </v-menu>

        <v-autocomplete
          v-model="currency"
          :items="currencies"
          outlined
          label="Currency"
        />
      </div>
    </v-card-text>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Actions">-->
    <template slot="dialogActions">
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        :disabled="!yacht"
        :loading="loadingServiceTasks"
        @click="submitCreateReport()"
      >
        Create Report
      </v-btn>
    </template>
    <!--</editor-fold>-->

    <service-cost-report-manage-items-dialog
      v-if="yacht && dateFrom.value && dateTo.value"
      :yacht="yacht"
      :service-tasks="serviceTasks"
      :visible.sync="manageItemsDialogVisible"
      :date-from="dateFrom.value"
      :date-to="dateTo.value"
      :currency="currency"
      @created="reportCreated"
    />
  </content-dialog>
</template>

<script>
  import ContentDialog from '@/components/helpers/Dialog'
  import validationScrollMixin from '@/mixins/validationScrollMixin'
  import YachtModalSelect from '@/components/service/helpers/YachtModalSelect'
  import ServiceCostReport from '@/models/ServiceCostReport'
  import CurrencyExchangeRate from '@/models/CurrencyExchangeRate'
  import ServiceCostReportManageItemsDialog
    from '@/components/service/serviceCostReports/ServiceCostReportManageItemsDialog'

  export default {
    name: 'ServiceCostReportCreateDialog',
    components: {
      ServiceCostReportManageItemsDialog,
      YachtModalSelect,
      ContentDialog
    },
    mixins: [
      validationScrollMixin
    ],
    props: {
      visible: Boolean
    },
    data() {
      return {
        yachtModalSelectVisible: false,
        loadingLastReport: false,
        yacht: null,
        lastReport: null,
        dateFrom: {
          picker: false,
          value: null,
          minDate: null
        },
        dateTo: {
          picker: false,
          value: null,
          minDate: null
        },
        currency: 'EUR',
        manageItemsDialogVisible: false,
        serviceTasks: [],
        loadingServiceTasks: false
      }
    },
    computed: {
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      },
      dateToValue: {
        get: function () {
          return this.dateTo.value ? this.$moment(this.dateTo.value).format('DD.MM.YYYY.') : ''
        },
        set: function (value) {
          if (!value) this.dateTo.value = null
        }
      },
      dateFromValue: {
        get: function () {
          return this.dateFrom.value ? this.$moment(this.dateFrom.value).format('DD.MM.YYYY.') : ''
        },
        set: function (value) {
          if (!value) this.dateFrom.value = null
        }
      }
    },
    watch: {
      yacht: function () {
        if (this.yacht) this.currency = this.yacht.country.currency

        this.fetchLastReport()
      },
      'dateFrom.value': function (value) {
        if (!value) {
          this.dateTo.minDate = null
          return
        }

        this.dateTo.minDate = this.$moment(value).add(1, 'day').format('YYYY-MM-DD')
        this.dateTo.value = this.$moment(value).add(1, 'month').format('YYYY-MM-DD')
      }
    },
    created() {
      this.currencies = CurrencyExchangeRate.currencies
    },
    methods: {
      async fetchLastReport() {
        if (!this.yacht) return

        try {
          this.loadingLastReport = true

          this.lastReport = await ServiceCostReport.api.findLastForYacht(this.yacht.id)

          this.dateFrom.value = this.$moment(this.lastReport.date_to).format('YYYY-MM-DD')
        } catch (e) {
          if (!e.response) {
            throw e
          }

          if (e.response && e.response.status !== 404) {
            throw e
          }
        } finally {
          this.loadingLastReport = false
        }
      },
      async submitCreateReport() {
        let validation = await this.$validator.validateAll()

        if (!validation) return

        this.fetchServiceTasksAndOpenManageDialog()
      },
      async fetchServiceTasksAndOpenManageDialog() {
        try {
          this.loadingServiceTasks = true

          let result = await ServiceCostReport.api.fetchServiceTasksWithCosts(this.yacht.id, this.dateFrom.value, this.dateTo.value, this.currency)

          if (result.items.length === 0) {
            this.$store.dispatch('alert', {
              text: 'There are no service tasks in the selected period',
              color: 'error'
            })
            return
          }

          this.serviceTasks = result.items

          this.manageItemsDialogVisible = true
        } catch (e) {
          this.$store.dispatch('alert', {
            text: 'Something went wrong',
            color: 'error'
          })

          throw e
        } finally {
          this.loadingServiceTasks = false
        }
      },
      reportCreated(report) {
        this.manageItemsDialogVisible = false
        this.modalVisible = false

        this.$emit('service-cost-report:created', report)
      }
    }
  }
</script>
